import {useEffect, useState} from "react";
import {theme} from "./MuiCustomization";
import {LinearProgress, Slider, ThemeProvider} from "@mui/material";

const SLIDER_FACET_KEYS = new Set([
    "price_range",
    "year_range",
    "mileage_range",
    "engine_capacity_range"
]);

export default function Filter({
    selectedFilters,
    onUpdateSelectedFilters,
    vehicleCategories,
    disabledFacetKeys = [],
    hiddenFacetKeys = [],
}) {

    const [facetGroups, setFacetGroups] = useState([]);
    const [facetsLoading, setFacetsLoading] = useState(false);
    const [sliderDraftValues, setSliderDraftValues] = useState({});
    const selectedFiltersSignature = JSON.stringify(selectedFilters);
    const hasFacetGroups = facetGroups.length > 0;

    useEffect(() => {
        let cancelled = false;

        setFacetsLoading(true);
        fetch("/api/catalog/facets", {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify({
                vehicleCategories,
                selected: selectedFilters
            })
        })
            .then(response => response.json())
            .then(data => {
                if (!cancelled) {
                    setFacetGroups(data.facets || []);
                    setFacetsLoading(false);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setFacetsLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [vehicleCategories, selectedFilters, selectedFiltersSignature]);

    function updateFacetOption(facetKey, facetValue) {
        if (disabledFacetKeySet.has(facetKey)) {
            return;
        }
        const currentFacetValues = selectedFilters[facetKey] || [];
        const hasValue = currentFacetValues.includes(facetValue);
        const nextFacetValues = hasValue
            ? currentFacetValues.filter(value => value !== facetValue)
            : [...currentFacetValues, facetValue].sort((leftValue, rightValue) =>
                leftValue.localeCompare(rightValue, undefined, {sensitivity: "base"})
            );

        const nextSelectedFilters = {...selectedFilters};
        if (nextFacetValues.length > 0) {
            nextSelectedFilters[facetKey] = nextFacetValues;
        } else {
            delete nextSelectedFilters[facetKey];
        }

        onUpdateSelectedFilters(nextSelectedFilters);
    }

    function updateSliderFacet(facetKey, sliderRange, orderedOptions) {
        if (disabledFacetKeySet.has(facetKey)) {
            return;
        }
        const [startIndex, endIndex] = sliderRange;
        const nextSelectedFilters = {...selectedFilters};

        if (!orderedOptions.length
            || (startIndex === 0 && endIndex === orderedOptions.length - 1)) {
            delete nextSelectedFilters[facetKey];
        } else {
            const fromValue = orderedOptions[startIndex]?.value;
            const toValue = orderedOptions[endIndex]?.value;
            if (fromValue && toValue) {
                nextSelectedFilters[facetKey] = [`${fromValue}..${toValue}`];
            }
        }

        onUpdateSelectedFilters(nextSelectedFilters);
    }

    function updateSliderDraftValue(facetKey, sliderValue) {
        setSliderDraftValues(currentDraftValues => ({
            ...currentDraftValues,
            [facetKey]: sliderValue
        }));
    }

    function resetFilters() {
        setSliderDraftValues({});
        onUpdateSelectedFilters({});
    }

    function removeFacetValue(facetKey, facetValue) {
        if (disabledFacetKeySet.has(facetKey)) {
            return;
        }
        if (isSliderFacet(facetKey)) {
            setSliderDraftValues(currentDraftValues => {
                const nextDraftValues = {...currentDraftValues};
                delete nextDraftValues[facetKey];
                return nextDraftValues;
            });
            const nextSelectedFilters = {...selectedFilters};
            delete nextSelectedFilters[facetKey];
            onUpdateSelectedFilters(nextSelectedFilters);
            return;
        }

        const currentFacetValues = selectedFilters[facetKey] || [];
        const nextFacetValues = currentFacetValues.filter(value => value !== facetValue);
        const nextSelectedFilters = {...selectedFilters};

        if (nextFacetValues.length > 0) {
            nextSelectedFilters[facetKey] = nextFacetValues;
        } else {
            delete nextSelectedFilters[facetKey];
        }

        onUpdateSelectedFilters(nextSelectedFilters);
    }

    const disabledFacetKeySet = new Set(disabledFacetKeys);
    const hiddenFacetKeySet = new Set(hiddenFacetKeys);
    const visibleFacetGroups = facetGroups.filter(group =>
        !hiddenFacetKeySet.has(group.key) && (
            isSliderFacet(group.key)
                ? Boolean(selectedFilters[group.key]?.length) || group.options.length > 1
                : group.options.some(option => option.selected) || group.options.length > 1
        )
    );

    const selectedChips = facetGroups.flatMap(group =>
        hiddenFacetKeySet.has(group.key)
            ? []
            : (isSliderFacet(group.key)
                ? buildSliderFacetChip(group)
                : (selectedFilters[group.key] || []).map(value => {
                    const option = group.options.find(groupOption => groupOption.value === value);
                    return {
                        key: group.key,
                        name: group.name,
                        value,
                        label: option?.label || value
                    };
                }))
    );

    if (facetsLoading && !hasFacetGroups) {
        return <ThemeProvider theme={theme}>
            <LinearProgress color="secondary"/>
        </ThemeProvider>;
    }

    return (
        <>
            <hr/>
            {!visibleFacetGroups.length
                ? <span>Нет информации для отображения фильтров.</span>
                : (
                    <div className="mb-4">
                        <a data-bs-toggle="collapse" href="#facetFilterBlock"
                           role="button" aria-expanded="false" aria-controls="facetFilterBlock">
                            <span className="icon-caret-down"> Фильтры каталога</span>
                        </a>
                        {facetsLoading && (
                            <ThemeProvider theme={theme}>
                                <div className="mt-3">
                                    <LinearProgress color="secondary"/>
                                </div>
                            </ThemeProvider>
                        )}
                                {selectedChips.length > 0 && (
                                    <div className="d-flex flex-wrap gap-2 mt-3">
                                        {selectedChips.map(chip => (
                                            <button
                                                key={`${chip.key}:${chip.value}`}
                                                type="button"
                                                className="btn btn-outline-secondary btn-sm"
                                                onClick={() => removeFacetValue(chip.key, chip.value)}
                                                disabled={disabledFacetKeySet.has(chip.key)}
                                            >
                                                {chip.name}: {chip.label} ×
                                            </button>
                                        ))}
                                    </div>
                        )}
                        <div className="collapse my-2" id="facetFilterBlock">
                            <div className="card card-body">
                                {visibleFacetGroups.map(group => (
                                    <div className="mb-4" key={group.key}>
                                        <div className="fw-semibold mb-2">{group.name}</div>
                                        {isSliderFacet(group.key)
                                            ? renderSliderFacet(group)
                                            : (
                                                <div style={{columnWidth: "220px", columnGap: "1.5rem"}}>
                                                    {group.options.map(option => {
                                                        const optionLabel = option.unit
                                                            ? `${option.value} ${option.unit}`
                                                            : option.value;
                                                        return (
                                                            <div
                                                                key={`${group.key}:${option.value}:${option.unit || ""}`}
                                                                style={{breakInside: "avoid", marginBottom: "0.75rem"}}
                                                            >
                                                                <div className="form-check">
                                                                    <input className="form-check-input"
                                                                           type="checkbox"
                                                                           id={`${group.key}-${option.value}`}
                                                                           value={option.value}
                                                                           checked={option.selected}
                                                                           disabled={disabledFacetKeySet.has(group.key) || (!option.enabled && !option.selected)}
                                                                           onChange={() => updateFacetOption(group.key, option.value)}/>
                                                                    <label className="form-check-label"
                                                                           htmlFor={`${group.key}-${option.value}`}>
                                                                        {option.label || optionLabel} ({option.count})
                                                                    </label>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )
            }

            <div className="d-flex flex-column mb-5">
                <div className="">
                    <p className="text-end">
                        <button className="btn btn-link pe-2 fw-semibold text-decoration-none" type="button"
                                onClick={resetFilters}>
                            Сбросить фильтр
                        </button>
                    </p>
                </div>
            </div>
        </>
    );

    function isSliderFacet(facetKey) {
        return SLIDER_FACET_KEYS.has(facetKey);
    }

    function buildSliderFacetChip(group) {
        const orderedOptions = group.options;
        const activeValue = selectedFilters[group.key]?.[0];

        if (!activeValue) {
            return [];
        }

        const sliderIndexes = resolveSliderIndexes(activeValue, orderedOptions);
        if (!sliderIndexes) {
            return [];
        }

        const [startIndex, endIndex] = sliderIndexes;
        const fromOption = orderedOptions[startIndex];
        const toOption = orderedOptions[endIndex];
        if (!fromOption || !toOption) {
            return [];
        }

        return [{
            key: group.key,
            value: activeValue,
            label: `${fromOption.label || fromOption.value} - ${toOption.label || toOption.value}`,
            name: group.name
        }];
    }

    function renderSliderFacet(group) {
        const orderedOptions = group.options;
        const committedSliderValue = resolveSliderIndexes(selectedFilters[group.key]?.[0], orderedOptions)
            || [0, Math.max(orderedOptions.length - 1, 0)];
        const sliderValue = sliderDraftValues[group.key] ?? committedSliderValue;

        return (
            <div
                style={{
                    paddingLeft: "clamp(16px, 4vw, 24px)",
                    paddingRight: "clamp(16px, 4vw, 24px)"
                }}
            >
                <Slider
                    value={sliderValue}
                    min={0}
                    max={Math.max(orderedOptions.length - 1, 0)}
                    step={1}
                    marks={orderedOptions.map((option, index) => ({
                        value: index,
                        label: index === 0 || index === orderedOptions.length - 1 ? (option.label || option.value) : ""
                    }))}
                    onChange={(_, value) => updateSliderDraftValue(group.key, value)}
                    onChangeCommitted={(_, value) => {
                        updateSliderDraftValue(group.key, value);
                        updateSliderFacet(group.key, value, orderedOptions);
                    }}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(value) => orderedOptions[value]?.label || orderedOptions[value]?.value || ""}
                    disableSwap
                />
                <div className="d-flex justify-content-between small text-muted">
                    <span>{orderedOptions[sliderValue[0]]?.label || ""}</span>
                    <span>{orderedOptions[sliderValue[1]]?.label || ""}</span>
                </div>
            </div>
        );
    }

    function resolveSliderIndexes(selectedValue, orderedOptions) {
        if (!orderedOptions.length) {
            return [0, 0];
        }

        if (!selectedValue || !selectedValue.includes("..")) {
            return null;
        }

        const [fromValue, toValue] = selectedValue.split("..");
        const exactStartIndex = orderedOptions.findIndex(option => option.value === fromValue);
        const exactEndIndex = orderedOptions.findIndex(option => option.value === toValue);

        if (exactStartIndex >= 0 && exactEndIndex >= 0) {
            return [Math.min(exactStartIndex, exactEndIndex), Math.max(exactStartIndex, exactEndIndex)];
        }

        const fromNumber = parseSliderNumericValue(fromValue);
        const toNumber = parseSliderNumericValue(toValue);
        const numericOptions = orderedOptions
            .map(option => parseSliderNumericValue(option.value));

        if (fromNumber === null || toNumber === null || numericOptions.some(value => value === null)) {
            return null;
        }

        let startIndex = numericOptions.findIndex(value => value >= fromNumber);
        if (startIndex < 0) {
            startIndex = orderedOptions.length - 1;
        }

        let endIndex = findLastIndex(numericOptions, value => value <= toNumber);
        if (endIndex < 0) {
            endIndex = 0;
        }

        if (startIndex > endIndex) {
            const nearestStartIndex = findNearestIndex(numericOptions, fromNumber);
            const nearestEndIndex = findNearestIndex(numericOptions, toNumber);
            return [Math.min(nearestStartIndex, nearestEndIndex), Math.max(nearestStartIndex, nearestEndIndex)];
        }

        return [startIndex, endIndex];
    }

    function parseSliderNumericValue(rawValue) {
        if (rawValue === undefined || rawValue === null) {
            return null;
        }

        const numericValue = Number.parseFloat(String(rawValue).replace(",", "."));
        return Number.isNaN(numericValue) ? null : numericValue;
    }

    function findLastIndex(values, predicate) {
        for (let index = values.length - 1; index >= 0; index -= 1) {
            if (predicate(values[index])) {
                return index;
            }
        }
        return -1;
    }

    function findNearestIndex(values, targetValue) {
        let nearestIndex = 0;
        let nearestDistance = Number.POSITIVE_INFINITY;

        values.forEach((value, index) => {
            const distance = Math.abs(value - targetValue);
            if (distance < nearestDistance) {
                nearestDistance = distance;
                nearestIndex = index;
            }
        });

        return nearestIndex;
    }
}
